import { useEffect, useState, useCallback } from 'react';
import { Box, Button, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Popover, Chip } from '@mui/material';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { getTodayInTz } from '../utils/dateUtils';
import './MyWorkflows.css';
import theme from '../theme';
import percent_image from '../assetts/images/percent_image.svg';
import StatusCard from './StatusCard';
import { getDashboardDataList, fetchCustomerCounts } from '../api/dashboardApi';
import { setDateRange } from '../redux/actions/dateTimeActions';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SearchComponent from './common/SearchBox';
import CustomTooltip from './reusableComponents/CustomTooltip/CustomTooltip';
import CustomPagination from './common/CustomPagination';
import { transformeddashboardtiles, transformedMyworkflowList } from '../utils/myworkflowUtils';
import CustomDaterangepicker from './reusableComponents/DateRangePicker/CustomDaterangepicker';
import Preloader from './common/Preloader';
import TagFilterComponent from './common/TagFilterComponent'
import ConfirmationDialog from './reusableComponents/ConfirmationDialog/ConfirmationDialog';
import CustomAlert from './reusableComponents/CustomAlert/CustomAlert';
import { TriggerWorkflowApi } from '../api/workflowApi';

const MyWorkflows = () => {
  const dispatch = useDispatch();
  const partnerCustomer = useSelector((state) => state.partner.partnerCustomer);
  const userPermissions = useSelector((state) => state.auth.userDetails?.userPermissions);
  const viewMode = useSelector((state) => state.partner.viewMode)
  const [searchTerm, setSearchTerm] = useState('');
  const { startDate, endDate } = useSelector((state) => state.dateTime.dateRange);
  const [tileData, setTileData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const navigate = useNavigate();
  let accessToken = useSelector((state) => state.auth.accessToken);
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(true)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [tableDataOrder, setTableDataOrder] = useState({
    order_type: "desc",
    order_field: "last_run_date"
  })
  const [showRunPopup, setShowRunPopup] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState({ id: null, title: '' });
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });
  const [tagAnchorEl, setTagAnchorEl] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  const [selectedFilterTags, setSelectedFilterTags] = useState([]);

  useEffect(() => {
    if (startDate && endDate) {
      return; // already set — don't override
    }

    const today = getTodayInTz();
    const defaultDateRange = {
      startDate: dayjs(today).subtract(7, 'day').format('YYYY-MM-DD'),
      endDate: today,
    };
    localStorage.setItem('dateRange', JSON.stringify(defaultDateRange));
    dispatch(setDateRange(defaultDateRange));
  }, []);

  useEffect(() => {
    const getCustomercounts = async () => {
      try {
        const data = await fetchCustomerCounts(startDate, endDate, viewMode, partnerCustomer?.customerId);
        const transformedTilesData = transformeddashboardtiles(data.customers[0]);
        setTileData(transformedTilesData.dashboardTiles);
        setLoader(false)
      } catch (e) {
        console.error("Failed to fetch customer counts:", e);
        setLoader(false)
      }
    };
    if (startDate && endDate) {
      getCustomercounts();
    }

  }, [startDate, endDate, viewMode, partnerCustomer?.customerId, partnerCustomer.name]);

  useEffect(() => {
    if (startDate && endDate) {
      getRowData(currentPage);
    }

  }, [searchTerm, startDate, endDate, tableDataOrder.order_field, tableDataOrder.order_type, currentPage, partnerCustomer.name, selectedFilterTags]);

  const getRowData = useCallback(async (page) => {
    try {
      setLoading(true)
      const data = await getDashboardDataList(accessToken, page, searchTerm, startDate, endDate, tableDataOrder.order_field, tableDataOrder.order_type, selectedFilterTags); // Call the API to fetch templates
      const transformedData = transformedMyworkflowList(data.results)
      setRowData(transformedData); // Set the 'results' array from the response
      setTotalPages(Math.ceil(data.count / 10))

      setLoading(false); // Set loading to false when data is fetched
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setLoading(false); // Set loading to false even on error
    }
  }, [
    accessToken,
    searchTerm,
    startDate,
    endDate,
    tableDataOrder.order_field,
    tableDataOrder.order_type,
    selectedFilterTags
  ]);
  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage);
  };

  const statuses = ['Success', 'In Progress', 'Warning', 'Error'];
  function getStatusColor(status, id = null) {
    // If id is provided, handle it specifically
    if (id !== null) {
      switch (id) {
        case 0:
          return theme.palette.success.main;
        case 1:
          return theme.palette.inprogress.main;
        case 2:
          return theme.palette.warning.main;
        case 3:
          return theme.palette.error.main;
        default:
          return theme.palette.grey[500];
      }
    }

    // Handle based on status if no id is provided
    switch (status) {
      case 'Success':
        return theme.palette.success.main;
      case 'In Progress':
        return theme.palette.inprogress.main;
      case 'Warning':
        return theme.palette.warning.main;
      case 'Error':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  }
  function formatRunNumber(run) {
    if (run >= 1000) {
      return (run / 1000).toFixed(0) + 'k'; // Convert to "k" format and keep one decimal point
    }
    return run.toString(); // Return as is for numbers less than 1000
  }
  const handleClickIntegration = () => {
    navigate('/integrations'); // Navigate to integrations page
  }
  const handleClickWorkflowLibrary = () => {
    navigate('/workflow-library');
  }

  const handleSchedule = (id, name) => {
    navigate(`/workflow-schedule/${id}`, {
      state: { name }
    });
  }
  const handleRunNow = async (workflowId) => {
    const response = await TriggerWorkflowApi(workflowId);
    setAlert((prev) => ({ ...prev, open: true, message: response.message, type: "success" }));
    setShowRunPopup(false)
    setTimeout(() => {
      navigate(`/process-flow/${workflowId}/all`);
    }, 1500);
  };

  const handleSort = (field) => {
    setTableDataOrder((prev) => ({
      order_field: field,
      order_type:
        prev.order_field === field && prev.order_type === "asc"
          ? "desc"
          : "asc"
    }));
    setCurrentPage(1)
  };
  const handleTagClick = (event, tags) => {
    event.stopPropagation(); // Prevent row click event
    setTagAnchorEl(event.currentTarget);
    setSelectedTags(tags || []);
  };

  const handleTagClose = () => {
    setTagAnchorEl(null);
  };

  const handleTagFilterToggle = (tag) => {
    setSelectedFilterTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  const handleClearTagFilters = () => {
    setSelectedFilterTags([]);
  };

  const tagPopoverOpen = Boolean(tagAnchorEl);
  if (loader) {
    return <Preloader />; // Show preloader while loading
  }

  return (
    <>
      <CustomAlert
        open={alert.open}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, open: false })}
      />
      <Box>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* {statusData.map((data, index) => ( */}
          {tileData.length > 0 && tileData?.map((data, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatusCard
                title={data.status}
                count={data.count}
                percentage={data.percentage}
                percent_image={percent_image}
              />
            </Grid>
          ))}
        </Grid>
        {userPermissions?.includes("customer_workflow-update") && <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          {/* Buttons */}
          <Grid item>
            <Box className="buttonGroup" display="flex" marginTop={5}>
              <Button
                variant="contained"
                sx={{
                  width: '140px',
                  height: '32px',
                  borderRadius: '24px',
                  backgroundColor: '#4F63BE',
                  boxShadow: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4F63BE',
                  },
                  '& .MuiButton-startIcon': {
                    borderRadius: '50%',
                    padding: '0px',
                  },
                }}
                startIcon={<AddCircleOutlinedIcon sx={{ borderRadius: '50%', marginLeft: '-5px' }} />}
                onClick={handleClickIntegration} // Open drawer on click
              >
                Integrations
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: '140px',
                  height: '32px',
                  borderRadius: '24px',
                  backgroundColor: '#7398C7',
                  boxShadow: 'none',
                  textTransform: 'none',
                  marginLeft: '5px',
                  '&:hover': {
                    backgroundColor: '#7398C7',
                  },
                  '& .MuiButton-startIcon': {
                    borderRadius: '50%',
                    padding: '0px',
                    marginLeft: '-16px', // Move icon to the left-most
                  },
                }}
                startIcon={
                  <AddCircleOutlinedIcon
                    sx={{
                      borderRadius: '50%',
                      fontSize: 20,
                      marginLeft: '0px', // No extra left margin on the icon itself
                    }}
                  />
                }
                onClick={handleClickWorkflowLibrary}
              >
                Workflows
              </Button>
            </Box>
          </Grid>


          {/* Right Group: Status Dots */}
          <Grid item>
            <Box display="flex" justifyContent="flex-end" gap={5}>
              {statuses.map((status, index) => (
                <Box key={index} display="flex" alignItems="center" sx={{ marginTop: 5 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '32px',
                      backgroundColor: getStatusColor(status),
                      marginRight: 1,
                    }}
                  />
                  <Typography variant="caption">{status}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>}
      </Box>


      <Box sx={{ mt: 5, border: '1.8px solid #ECEEF6', borderRadius: '25px', p: 2 }}>

        {/* Date filter and Search box can go here */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: "wrap", marginLeft: "14px" }}>

          {/* Left side: Heading */}

          <Typography variant="h4" sx={{ fontWeight: 600 }}> {/* Adjusted Typography variant as needed */}

            Workflows

          </Typography>


          {/* Right side: Filter and Search Box */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: "wrap" }}>
            {/* Filter Box */}
            <TagFilterComponent selectedTags={selectedFilterTags} onTagToggle={handleTagFilterToggle} onClearAll={handleClearTagFilters} setCurrentPage={setCurrentPage} />
            <Box
              sx={{
                minWidth: 115,
                minHeight: 32,

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'left',
                padding: '10px',
                paddingLeft: "0px",
                fontWeight: 600,
                borderRadius: '24px',
                boxSizing: 'border-box',
                color: '#4F63BE',

              }}
            >

              <CustomDaterangepicker />
            </Box>


            <SearchComponent searchTerm={searchTerm} setSearchTerm={setSearchTerm} setCurrentPage={setCurrentPage} />
          </Box>
        </Box>
        <Box sx={{ mt: 3 }}>
          {
            loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <CircularProgress />
              </Box>

            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none' }}>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: 0, border: 'none' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...theme.typography.tableCellHeader, cursor: "pointer" }} onClick={() => handleSort("workflow_name")}>Name&nbsp;  {tableDataOrder.order_field === "workflow_name" && (tableDataOrder.order_type === "asc" ? <ArrowUpwardIcon fontSize="14px" /> : <ArrowDownwardIcon fontSize="14px" />)}  </TableCell>
                      <TableCell sx={{ ...theme.typography.tableCellHeader, cursor: "pointer" }} onClick={() => handleSort("status")}>Status &nbsp;{tableDataOrder.order_field === "status" && (tableDataOrder.order_type === "asc" ? <ArrowUpwardIcon fontSize="14px" /> : <ArrowDownwardIcon fontSize="14px" />)}</TableCell>
                      <TableCell sx={{ ...theme.typography.tableCellHeader, cursor: "pointer" }} onClick={() => handleSort("last_run_date")}>Last Run&nbsp;{tableDataOrder.order_field === "last_run_date" && (tableDataOrder.order_type === "asc" ? <ArrowUpwardIcon fontSize="14px" /> : <ArrowDownwardIcon fontSize="14px" />)}</TableCell>
                      <TableCell sx={{ ...theme.typography.tableCellHeader }}>Total Runs</TableCell>
                      {userPermissions?.includes("customer_workflow-update") && <TableCell sx={{ ...theme.typography.tableCellHeader }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowData && rowData.length > 0 ? (
                      rowData.map((row, index) => (

                        <TableRow key={index} sx={{
                          border: 'none',
                          '&:hover': {
                            backgroundColor: '#f0f0f0', // Optional: Change background color on hover
                          },
                        }}>

                          <TableCell sx={{
                            borderBottom: 'none', ...theme.typography.tableCellData,
                            // '&:hover': {
                            //   backgroundColor: '#f0f0f0'
                            // },
                          }} // Optional: Change background color on hover }}
                            style={{ cursor: 'pointer', display: "flex", gap: "1rem", alignItems: "center" }}
                            onClick={() => { navigate(`/process-flow/${row.id}/all`) }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {row.name}
                              {row.tag_names && row.tag_names.length > 0 && (
                                <Box
                                  onClick={(e) => handleTagClick(e, row.tag_names)}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    backgroundColor: '#E9EFF7',
                                    '&:hover': {
                                      backgroundColor: '#D4E3F5',
                                    },
                                  }}
                                >
                                  <LabelOutlinedIcon Icon
                                    sx={{
                                      fontSize: 16,
                                      color: '#4F63BE'
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      ml: 0.5,
                                      color: '#4F63BE',
                                      fontWeight: 600
                                    }}
                                  >
                                    {row.tag_names.length}
                                  </Typography>

                                </Box>
                              )}</Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Box
                              sx={{
                                minWidth: 62,

                                backgroundColor: row.status === 'Active' ? '#E9EFF7' : '#EDEDED',
                                display: 'inline-block',
                                textAlign: 'center', // Centers the text inside the box
                                padding: '6px 8px',

                                fontWeight: 600,
                                borderRadius: '16px',
                                boxSizing: 'border-box',
                                color: row.status === 'Active' ? '#4F63BE' : '#6B6B6B',
                              }}
                            >
                              {row.status}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none', ...theme.typography.tableCellData }}>
                            {row.last_run}
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Box display="flex" justifyContent="flex-start" gap={1}>
                              {Object.entries(row.total_runs).map(([key, run], idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    color: getStatusColor(key, idx),
                                    display: 'inline-block',
                                    width: '3ch',
                                    textAlign: 'left',
                                    borderBottom: `5px solid ${getStatusColor(key, idx)}`,
                                    paddingBottom: '2px',
                                    fontWeight: 600,
                                    cursor: "pointer"
                                  }}
                                  onClick={() => { navigate(`/process-flow/${row.id}/${key}`) }}
                                >
                                  {formatRunNumber(run)}
                                </Box>
                              ))}
                            </Box>
                          </TableCell>
                          {userPermissions?.includes("customer_workflow-update") && <TableCell sx={{ borderBottom: 'none' }}>
                            <Box
                              sx={{
                                display: 'flex', // Flex to align items in a row
                                gap: 2, // Add some spacing between the two boxes
                              }}
                            >
                              <CustomTooltip title="View">
                                <Box
                                  sx={{
                                    minWidth: 34,
                                    minHeight: 34,
                                    backgroundColor: '#E9EFF7',
                                    display: 'flex', // Use flex to align items
                                    justifyContent: 'center', // Center horizontally
                                    alignItems: 'center', // Center vertically
                                    textAlign: 'center',
                                    padding: '2px 2px',
                                    fontWeight: 600,
                                    borderRadius: '40px',
                                    boxSizing: 'border-box',
                                    color: '#4F63BE',
                                    cursor: 'pointer'
                                  }}

                                  onClick={() => navigate(`/workflow-builder/workflow/${row.id}`)}
                                >
                                  <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                </Box>
                              </CustomTooltip>
                              <CustomTooltip title="Schedule">
                                <Box
                                  onClick={(e) => handleSchedule(row.id, row.name)}
                                  sx={{
                                    minWidth: 34,
                                    minHeight: 34,
                                    backgroundColor: '#E9EFF7',
                                    display: 'flex', // Use flex to align items
                                    justifyContent: 'center', // Center horizontally
                                    alignItems: 'center', // Center vertically
                                    textAlign: 'center',
                                    padding: '2px 2px',
                                    fontWeight: 600,
                                    borderRadius: '40px',
                                    boxSizing: 'border-box',
                                    color: '#4F63BE'
                                  }}

                                >
                                  <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
                                </Box>
                              </CustomTooltip>
                              <CustomTooltip title="Run Now">
                                <Box
                                  onClick={() => { setSelectedWorkflow({ id: row.id, title: row.name }); setShowRunPopup(true) }}
                                  sx={{
                                    minWidth: 34,
                                    minHeight: 34,
                                    backgroundColor: '#E9EFF7',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    padding: '2px 2px',
                                    fontWeight: 600,
                                    borderRadius: '40px',
                                    boxSizing: 'border-box',
                                    color: '#4F63BE',
                                    opacity: row.status === 'Active' ? '1' : '0.5',
                                    cursor: row.status === 'Active' ? 'pointer' : 'not-allowed',
                                    pointerEvents: row.status === 'Active' ? 'auto' : 'none',
                                  }}

                                >
                                  <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                                </Box>
                              </CustomTooltip>
                            </Box>
                          </TableCell>}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', padding: '16px' }}>
                          No data available
                        </TableCell>
                      </TableRow>
                    )}

                  </TableBody>
                </Table>
              </TableContainer>
            )
          }

        </Box>

      </Box>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignContent='center'
        sx={{
          width: '100%',
          position: 'relative',

          bottom: -30,
          alignItems: "center",
          border: '0px solid #ECEEF6',
        }}>

        <CustomPagination
          totalPages={totalPages}
          curPage={currentPage}
          onPageChange={handlePageChange}
        ></CustomPagination>

      </Box>
      <Popover
        open={tagPopoverOpen}
        anchorEl={tagAnchorEl}
        onClose={handleTagClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: "12px",
            padding: "12px",
            position: "relative",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            maxWidth: "300px",
            marginLeft: "12px",
            overflow: "visible",
            minWidth: "150px",
            width: "fit-content",
            "&::before": {
              content: '""',
              position: "absolute",
              left: "-6px",
              top: "50%",
              transform: "translateY(-50%) rotate(45deg)",
              width: "12px",
              height: "12px",
              backgroundColor: "white",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              zIndex: 0,
            },
          },
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#4F63BE" }}
          >
            Tags
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {selectedTags.length > 0 ? (
              selectedTags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: "#E9EFF7",
                    color: "#4F63BE",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#D4E3F5",
                    },
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No tags available
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>
      {showRunPopup && (
        <ConfirmationDialog
          isOpen={showRunPopup}
          onClose={() => setShowRunPopup(false)}
          confirmText={"Yes"}
          onConfirm={() => handleRunNow(selectedWorkflow.id)}
          variant="Success"
          title={`Run ${selectedWorkflow.title}`}
          message={"Would you like to run this workflow?"}
          onCancel={() => setShowRunPopup(false)}
          cancelText="No"
          showIcon={true}
          position="center"
          size="small"
          closeOnOverlayClick={!showRunPopup}
          closeOnEscape={!showRunPopup}
          blurBackground={true}
        />
      )}
    </>
  );
};


export default MyWorkflows;